import { toast } from "react-toastify";
import environment from "../../environment";
import loader from "../loader";
import methodModel from "../methods";
import { encrypt, decrypt } from "./Encrypt";

const baseUrl = environment.api;

const getHeaders = (extra = {}) => {
  const token = localStorage.getItem("token");

  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
    ...extra,
  };
};

const handleError = (data, hideError) => {
  const is401 = data?.error?.code === 401 || data?.status === 401;
  if (is401) {
    localStorage.removeItem("persist:admin-app");
    localStorage.removeItem("token");
    methodModel.route("/login");
    document.getElementById("logoutBtn")?.click();

    // Prevent multiple 401 toasts
    if (toast.isActive("api-401-error")) return;
  }

  const message = is401 ? 'Please login again Session expired!' : (
    data?.error?.message ||
    data?.message ||
    "Server Error");

  if (!hideError) {
    if (is401) {
      toast.error(message, { toastId: "api-401-error" });
    } else {
      toast.error(message);
    }
  }
};

class ApiClient {
  static async request(url, options = {}, hideError = false) {
    try {
      const res = await fetch(url, options);
      const data = await res.json();

      // 🔓 Decrypt response
      if (typeof data?.data === "string") {
        const decryptedData = decrypt(data?.data || "");
        data.data = decryptedData?.data || decryptedData || data?.data;

        if (decryptedData?.total) {
          data.total = decryptedData?.total;
        }
      }

      if (!res.ok) {
        handleError({ ...data, status: res.status }, hideError);
        return { ...data, success: false };
      }

      return data;
    } catch (error) {
      loader(false);
      toast.error("Network Error", { toastId: "network-error" });
      throw error;
    }
  }

  static get(url, params = {}, hideError = false) {
    const encryptedParams = encrypt(params);

    const query = new URLSearchParams({ data: encryptedParams }).toString();
    const fullUrl = query ? `${baseUrl}${url}?${query}` : baseUrl + url;

    return this.request(
      fullUrl,
      {
        method: "GET",
        headers: getHeaders(),
      },
      hideError
    );
  }

  static post(url, payload, hideError = false) {
    const encryptedPayload = { data: encrypt(payload) };

    return this.request(
      baseUrl + url,
      {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(encryptedPayload),
      },
      hideError
    );
  }

  static put(url, payload) {
    const encryptedPayload = { data: encrypt(payload) };

    return this.request(baseUrl + url, {
      method: "PUT",
      headers: getHeaders(),
      body: JSON.stringify(encryptedPayload),
    });
  }

  static patch(url, payload) {
    const encryptedPayload = { data: encrypt(payload) };

    return this.request(baseUrl + url, {
      method: "PATCH",
      headers: getHeaders(),
      body: JSON.stringify(encryptedPayload),
    });
  }

  static delete(url, params = {}, hideError = false) {
    console.log(params, "params")
    const encryptedParams = encrypt(params);

    const query = new URLSearchParams({ data: encryptedParams }).toString();
    const fullUrl = query ? `${baseUrl}${url}?${query}` : baseUrl + url;
    return this.request(fullUrl, {
      method: "DELETE",
      headers: getHeaders(),
    }, hideError);
  }

  static allApi(url, params, method = "get", hideError = false) {
    switch (method.toLowerCase()) {
      case "get":
        return this.get(url, params, hideError);

      case "post":
        return this.post(url, params, hideError);

      case "put":
        return this.put(url, params);

      case "patch":
        return this.patch(url, params);

      case "delete":
        return this.delete(url);

      default:
        throw new Error("Unsupported method");
    }
  }

  static async postFormData(url, params) {
    const formData = new FormData();

    Object.keys(params).forEach((key) => {
      formData.append(key, params[key]);
    });

    return this.request(baseUrl + url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: formData,
    });
  }

  static async multiImageUpload(url, files, key = "file") {
    const formData = new FormData();

    Array.from(files).forEach((file) => {
      formData.append(key, file);
    });

    return this.request(baseUrl + url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: formData,
    });
  }
}

export default ApiClient;